/**
 * Data normalisation and sort from product tags => labels and handles
 * Mainly related to
 * - Brands
 * - Genders
 * - Categories
 * - Subcategories
 */
import { Label } from '../ProductListingPage/utils';

const data = {
  gender: [
    'mens',
    'womens',
    'kids',
    'unisex',
    'generic',
    'na'
  ],
  category: [
    'living',
    'activewear',
    'purch-music-style-based',
    'jewellery',
    'headwear',
    'accessories',
    'footwear',
    'tops',
    'sets',
    'bottoms',
    'non-stocked-items',
    'purch-not-for-sale-style-based',
  ],
  subcategory: {
    tops: [
      'ls-tees',
      'ss-tees',
      'party-shirt',
      'crop-singlet',
      'crop-tee',
      'muscle-tees',
      'crops',
      'crop',
      'singlets',
      'tank',
      'hood',
      'jacket',
      'jersey',
      'jumpsuits',
      'knitwear',
      'loungewear-tops',
      'button-up',
      'dress',
      'cardigan',
      'crewneck',
      'crewnecks',
      'polos',
      'tees',
      'outer-wear',
      'shirts',
      'bandeau',
      'bodysuit',
      'bras',
      'active-bra',
      'onesie',
      'hardwood',
      'replica',
      'rev30',
      'sets',
      'underwear',
      'misc-accessories',
      'misc-tops',
      'swingman',
    ],
    bottoms: [
      'jeans',
      'jogger',
      'leggings',
      'trackpant',
      'pants-striped',
      'pants',
      'overalls',
      'basketball-shorts',
      'basketballshorts',
      'beach-short',
      'bike-shorts',
      'cargo-shorts',
      'denim-shorts',
      'activewear',
      'loungewear-bottoms',
      'shorts',
      'skirt',
      'tights',
      'underwear',
    ],
    footwear: [
      'hi-top',
      'lows',
      'mids',
      'slip-on',
    ],
    headwear: [
      '5-panel',
      '940-a-frame',
      'beanies',
      'bucket-hats',
      'buckets-boonies',
      'face-mask',
      'fedora',
      'fitted',
      'full-brim',
      'golfer',
      'pro-crown',
      'snapback',
      'strapback',
      'stretch-fit',
      'trucker',
      'velcro-strap',
      'visor',
      'other',
      'original-fit',
      'nfs-costed',
      'misc-hats',
    ],
    jewellery: [
      'anklet',
      'bracelet',
      'chain',
      'earrings',
      'necklace',
      'pendant',
      'ring',
      'sunnies',
      'watches',
      'jewellery',
    ],
    accessories: [
      'bandana',
      'belts',
      'bracelet',
      'earrings',
      'hair-care',
      'necklace',
      'pendant',
      'ring',
      'sidebags',
      'socks',
      'strapback',
      'sunnies',
      'underwear',
      'wallets',
      'watches',
      'bags',
      'misc-accessories',
      'mobile-accessories',
      'caps-accessories',
      'jewellery',
    ],
    'purch-music-style-based': [
      'vinyls',
    ],
    'purch-not-for-sale-style-based': [
      'nfs-costed',
      'nfs-non-costed',
    ],
    'non-stocked-items': [
      'cbr-financial-item',
      'misc',
    ],
  },
};

const rareCategoryHandles = {
  activewear: 'activewear',
  living: 'living',
  na: 'mystery-bundles',
  'non-stocked-items': 'DO_NOT_RENDER',
  'purch-music-style-based': 'music-tour-merch',
  'purch-not-for-sale-style-based': 'not-for-sale',
  sets: 'sets',
};

/**
 * Get the name of category that is ranked higher in above data mapping
 * @param {string} category
 * @param {string} topRankingCategory
 * @returns {string}
 */
const getCategoryInHigherRanking = (category, topRankingCategory) => (
  data.category.indexOf(category) < data.category.indexOf(topRankingCategory)
    ? category
    : topRankingCategory
);

/**
 * Get the name of gender that is ranked higher in above data mapping
 * @param {string} gender
 * @param {string} topRankingGender
 * @returns {string}
 */
const getGenderInHigherRanking = (gender, topRankingGender) => (
  data.gender.indexOf(gender) < data.gender.indexOf(topRankingGender)
    ? gender
    : topRankingGender
);

/**
 * Get the name of subcategory that is ranked higher in above data mapping
 * @param {Object} subcategory: {name: xxx, category: xxx}
 * @param {Object} topRankingSubcategory: {name: xxx, category: xxx}
 * @returns {Object} {name: xxx, category: xxx}
 */
const getSubCategoryInHigherRanking = (subcategory, topRankingSubcategory) => {
  // Both subcategories are under the same category, compare the index, lower the higher
  if (subcategory.category === topRankingSubcategory.category) {
    const subcategories = data.subcategory[subcategory.category];
    if (!subcategories) return topRankingSubcategory;
    return subcategories.indexOf(subcategory.name) < subcategories.indexOf(topRankingSubcategory.name)
      ? subcategory
      : topRankingSubcategory;
  }

  // Can not find the mapping of the new subcategory, keep the old one
  if (!data.subcategory[subcategory.category]?.includes(subcategory.name)) {
    return topRankingSubcategory;
  }

  // Found the mapping of the new subcategory, but can not find the old one (possible if the old one was the first in the tag list), keep the new one
  if (!data.subcategory[topRankingSubcategory.category]?.includes(topRankingSubcategory.name)) {
    return subcategory;
  }

  // Can find both in the mapping, then compare the main category's ranking, the lower the higher
  return data.category.indexOf(subcategory.category) < data.category.indexOf(topRankingSubcategory.category)
    ? subcategory
    : topRankingSubcategory;
};

/**
 * Convert/normalise product tags to labels and handles for Gender, Brand, Category and SubCategory
 * If a product is tagged multiple times with the same type, this function also finds the top ranking one using
 * the ranking map defined above
 * @param {Array} tags - product tags
 * @returns {{
     brand: string,
     gender: string,
     category: string,
     categoryLabel: string,
     categoryHandle: string,
     subCategoryLabel: string,
     subCategoryHandle: string,
     isRareCategory: boolean,
   }}
 */
export const tagsToHandlesAndLabels = (tags) => {
  // The only tag prefixes/types that we care about
  const tagTypes = ['brand', 'gender', 'category'];

  // Normalise data from product tags and category hierarchies
  const { brand, gender, category, subcategories } = tags.reduce((acc, tag) => {
    const foundTagType = tagTypes.find(type => tag.startsWith(`${type}-`));
    // Disregard all other tag types
    if (!foundTagType) return acc;

    if (foundTagType === 'category') {
      const tagValue = tag.replace('category-', '');
      // subcategory
      if (tagValue.includes('--')) {
        const [myCategory, subcategory] = tagValue.split('--');
        acc.subcategories.push({ name: subcategory, category: myCategory });
        return acc;
      }

      // category
      if (!acc.category) return { ...acc, category: tagValue };
      return {
        ...acc,
        category: getCategoryInHigherRanking(tagValue, acc.category)
      };
    }
    if (foundTagType === 'gender') {
      const tagValue = tag.replace('gender-', '');
      if (!acc.gender) return { ...acc, gender: tagValue };
      return {
        ...acc,
        gender: getGenderInHigherRanking(tagValue, acc.gender),
      };
    }

    // everything else, gender or brand
    return {
      ...acc,
      [foundTagType]: tag.replace(`${foundTagType}-`, '')
    };
  }, {
    brand: '',
    gender: '',
    category: '',
    subcategories: []
  });

  // Get the top priority subcategory. It is an object which also includes its main category
  const subcategory = subcategories.reduce((acc, subcategoryObj) => {
    if (!acc) return subcategoryObj;
    return getSubCategoryInHigherRanking(subcategoryObj, acc);
  }, null);

  // Go find the translated category label if defined in locale file
  const subcategoryTitle = subcategory && Label.getLocaleString(subcategory.name, 'subcategory');
  const categoryTitle = category && Label.getLocaleString(category, 'category');

  /**
   * Some categories like NFS, gifting, mystery bundles and music are rare, and
   * they don't have subcategories collections created in Shopify
   * Some of them have messed up m3 category handles that Shopify has a more SEO friendly version
   * Those are defined and maintained in the mapping file, so we can check and act accordingly
   */
  const rareCategoryHandle = category && rareCategoryHandles[category];

  return {
    brand,
    gender,
    category,
    subCategoryHandle: gender && gender !== 'na'
      && subcategory
      && subcategoryTitle !== categoryTitle // If subcategory and category shares the same name, it is redundant to render the subcategory button
      && !rareCategoryHandles[subcategory.category] // if subcategory's category is a rare category, then don't render the subcategory button
      && `${gender}-${subcategory.category}-${subcategory.name}`,
    subCategoryLabel: Label.getLocaleString(subcategory?.name, 'subcategory'),
    categoryHandle: gender && gender !== 'na'
      && category
      && (
        rareCategoryHandle && rareCategoryHandle !== 'DO_NOT_RENDER'
          ? rareCategoryHandle // links to the rare category handle
          : `${gender}-${category}`
      ),
    categoryLabel: Label.getLocaleString(category, 'category'),
    isRareCategory: Boolean(rareCategoryHandle),
  };
};
